import type { PillColor } from 'components/pill/Pill';
import type { TranslationKey } from 'src/translation/types';

/**
 * Suivo's `unit-status.summary`, verbatim.
 *
 * One table for the whole tracking vocabulary: the map's discs, the table's pills and the trailer page's
 * panel all colour a status through here, so the three cannot disagree about what a colour means — and a
 * provider state we do not know is one line rather than a conditional in each view.
 */
export type TelemetryStatus = 'MOVING' | 'PARKED' | 'IDLING' | 'IMMOBILIZER' | 'OFFLINE' | 'IN_OPERATION';

export interface StatusPill {
  color: PillColor;
  labelKey: TranslationKey;
}

export const STATUS_PILL: Record<TelemetryStatus, StatusPill> = {
  MOVING: { color: 'positive', labelKey: 'liveTracking.status.moving' },
  PARKED: { color: 'info', labelKey: 'liveTracking.status.parked' },
  IDLING: { color: 'warning', labelKey: 'liveTracking.status.idling' },
  IMMOBILIZER: { color: 'negative', labelKey: 'liveTracking.status.immobilized' },
  OFFLINE: { color: 'neutral', labelKey: 'liveTracking.status.offline' },
  IN_OPERATION: { color: 'primary', labelKey: 'liveTracking.status.inOperation' },
};

function isTelemetryStatus(value: string): value is TelemetryStatus {
  return value in STATUS_PILL;
}

/** The pill for a status as the provider sent it, or null for one we have no vocabulary for. */
export function statusPill(status: string | null | undefined): StatusPill | null {
  if (!status || !isTelemetryStatus(status)) {
    return null;
  }
  return STATUS_PILL[status];
}

/**
 * How a marker is tinted for a status colour.
 *
 * Filled discs with a white icon, the way the order map draws its stops and its truck: a marker sits on
 * photographic map tiles, where a pale chip disappears into the background. The quiet states take the
 * inverse surface because the theme has no solid neutral — `--color-neutral-subtle` is #f5f5f5, which is
 * a badge colour, not a disc.
 *
 * Whole class names, because Tailwind v4 finds utilities by scanning source text: `bg-${color}` would
 * compile to nothing.
 */
export const MARKER_TINT: Record<PillColor, { disc: string; icon: string }> = {
  positive: { disc: 'bg-positive', icon: 'text-white' },
  info: { disc: 'bg-info', icon: 'text-white' },
  warning: { disc: 'bg-warning', icon: 'text-white' },
  negative: { disc: 'bg-negative', icon: 'text-white' },
  primary: { disc: 'bg-brand', icon: 'text-white' },
  neutral: { disc: 'bg-surface-inverse', icon: 'text-white' },
  default: { disc: 'bg-surface-inverse', icon: 'text-white' },
  muted: { disc: 'bg-surface-inverse', icon: 'text-white' },
};
